/** Ledger item 5, "On save": what the save will do, from the preview of the current
 *  draft — or, while that loads, the catalog estimate (labelled as such, no keys). A draft
 *  the preview asks nothing for yet (`preview.idle`: only a row still being typed) keeps
 *  the estimate with a settled note, never "loading".
 *
 *  - Events, with the Emit · Suppress switch (default Emit, D5); guest-visible ones
 *    flagged with an icon and the words. A record kind whose catalog declares no events
 *    (no estimate, no events switch, no action firing one — the catalog records) has no
 *    Events row, unless a preview names one.
 *  - Caches & syncs: flushed effects, automatic effects and other after-commit work.
 *  - External: writes to systems outside the database.
 *  Every line names its keys ("Check-in manifest · Thu 2 Oct 09:30 and Fri 3 Oct 09:30").
 */
import BoltIcon from '@mui/icons-material/Bolt';
import CachedIcon from '@mui/icons-material/Cached';
import CallMadeIcon from '@mui/icons-material/CallMade';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

import { humanizeCode } from '../editor/format';
import { useRecordEditor } from '../editor/RecordEditorProvider';
import type { RecordEditorConfig, RecordOption, RecordPreview } from '../editor/types';

import { eventFires, type EstimateLine } from './model';

function keysText(labels: string[] | undefined, truncated?: boolean): string {
  if (!labels || labels.length === 0) return '';
  const head = labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}` : labels[0]!;
  return truncated ? `${head} and more` : head;
}

function Line({
  icon,
  children,
  muted,
  testId,
}: {
  icon: ReactNode;
  children: ReactNode;
  muted?: boolean;
  testId?: string;
}) {
  return (
    <Box
      component="li"
      data-onsave={testId}
      sx={{
        display: 'flex',
        gap: 0.75,
        alignItems: 'flex-start',
        typography: 'caption',
        lineHeight: 1.5,
        color: muted ? 'text.secondary' : 'text.primary',
      }}
    >
      <Box component="span" aria-hidden sx={{ display: 'inline-flex', mt: '2px', color: 'text.secondary' }}>
        {icon}
      </Box>
      <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {children}
      </Box>
    </Box>
  );
}

function Code({ children, struck }: { children: ReactNode; struck?: boolean }) {
  return (
    <Box
      component={struck ? 's' : 'code'}
      sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.8125rem' }}
    >
      {children}
    </Box>
  );
}

export function GuestVisible() {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontWeight: 600, ml: 0.5 }}>
      <NotificationsActiveOutlinedIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main' }} />
      guest-visible
    </Box>
  );
}

function SubHead({ children }: { children: ReactNode }) {
  return (
    <Typography variant="caption" component="h5" color="text.secondary" sx={{ fontWeight: 600, m: 0, mt: 0.5 }}>
      {children}
    </Typography>
  );
}

const LIST_SX = { listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 } as const;

/** Whether a record kind's saves can fire events at all: its catalog estimates them, offers
 *  the Emit · Suppress switch or an entity's event switch, or has an action that fires one. */
export function declaresEvents(
  config: Pick<RecordEditorConfig, 'estimateEvents' | 'entityEventSwitch' | 'actions'>,
  updateEventsOption: RecordOption | null,
): boolean {
  return (
    Boolean(config.estimateEvents || config.entityEventSwitch || updateEventsOption) ||
    config.actions.some((a) => a.events.length > 0)
  );
}

export function LedgerOnSave({
  estimate,
}: {
  estimate: { events: EstimateLine[]; effects: EstimateLine[] };
}) {
  const { preview, updateEventsOption, updateEvents, setUpdateEvents, config } = useRecordEditor();
  const plan: RecordPreview | null = preview.data;
  const showEvents = declaresEvents(config, updateEventsOption) || (plan?.events.length ?? 0) > 0;
  const statusLabel = config.eventStatusLabel ?? ((s: string) => humanizeCode(s).toLowerCase());
  const suppressed = updateEvents === 'suppress';

  const effectLines = plan
    ? [
        ...Object.entries(plan.effects).map(([code, e]) => ({
          code,
          label: e.label,
          keys:
            keysText(e.key_labels, e.truncated) ||
            (e.kind === 'automatic' && e.count > 0 ? `${e.count} ${e.count === 1 ? 'record' : 'records'}` : ''),
        })),
        ...plan.follow_ups.map((s) => ({
          code: s.code,
          label: s.label,
          keys: [s.detail, keysText(s.key_labels)].filter(Boolean).join(' · '),
        })),
      ]
    : estimate.effects.map((e) => ({ code: e.code, label: e.label, keys: '' }));

  return (
    <Box sx={{ display: 'grid', gap: 0.75 }} aria-busy={preview.pending || undefined}>
      <Box sx={{ height: 2 }}>
        {preview.pending && <LinearProgress aria-label="Updating what the save does" sx={{ height: 2 }} />}
      </Box>

      {showEvents && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <SubHead>Events</SubHead>
            {updateEventsOption && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={updateEvents}
                onChange={(_, v: string | null) => v && setUpdateEvents(v)}
                aria-label="Update events"
                sx={{ ml: 'auto', '& .MuiToggleButton-root': { py: 0.125, px: 1, textTransform: 'none' } }}
              >
                {updateEventsOption.values.map((v) => (
                  <ToggleButton key={String(v.value)} value={String(v.value)}>
                    {v.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          </Box>
          <Box component="ul" sx={LIST_SX}>
            {plan
              ? plan.events.map((e) => {
                  const fires = eventFires(e.status);
                  return (
                    <Line key={`${e.code}:${e.entity}:${e.id}`} icon={<BoltIcon sx={{ fontSize: 14 }} />} muted={!fires} testId="event">
                      <Code struck={!fires}>{e.code}</Code> · {e.entity} #{e.id} · {statusLabel(e.status)}
                      {e.guest_visible && fires && <GuestVisible />}
                      {e.detail ? ` — ${e.detail}` : ''}
                    </Line>
                  );
                })
              : estimate.events.map((e) => {
                  const off = suppressed && e.suppressible !== false;
                  return (
                    <Line key={e.code} icon={<BoltIcon sx={{ fontSize: 14 }} />} muted={off} testId="event">
                      <Code struck={off}>{e.code}</Code>
                      {off ? ' · suppressed' : e.guestVisible && <GuestVisible />}
                    </Line>
                  );
                })}
            {(plan ? plan.events.length : estimate.events.length) === 0 && (
              <Typography component="li" variant="caption" color="text.secondary">
                No events
              </Typography>
            )}
          </Box>
        </>
      )}

      <SubHead>Caches &amp; syncs</SubHead>
      <Box component="ul" sx={LIST_SX}>
        {effectLines.map((l) => (
          <Line key={l.code} icon={<CachedIcon sx={{ fontSize: 14 }} />} testId="effect">
            {l.label}
            {l.keys ? ` · ${l.keys}` : ''}
          </Line>
        ))}
        {effectLines.length === 0 && (
          <Typography component="li" variant="caption" color="text.secondary">
            None
          </Typography>
        )}
      </Box>

      {plan && plan.external.length > 0 && (
        <>
          <SubHead>External</SubHead>
          <Box component="ul" sx={LIST_SX}>
            {plan.external.map((s) => (
              <Line key={s.code} icon={<CallMadeIcon sx={{ fontSize: 14 }} />} testId="external">
                {s.label}
                {s.key_labels?.length ? ` · ${keysText(s.key_labels)}` : s.detail ? ` · ${s.detail}` : ''}
              </Line>
            ))}
          </Box>
        </>
      )}

      {plan && plan.warnings.length > 0 && (
        <Box component="ul" sx={LIST_SX}>
          {plan.warnings.map((w) => (
            <Line key={w} icon={<WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />} testId="warning">
              {w}
            </Line>
          ))}
        </Box>
      )}

      {!plan && (
        <Typography variant="caption" color="text.secondary" data-onsave="estimate-note">
          {preview.failed
            ? 'The preview is unavailable — this is the catalog estimate only.'
            : preview.idle
              ? 'Estimated from the catalog — the full plan follows once the change is filled in.'
              : 'Estimated from the catalog — the full plan with its keys is loading.'}
        </Typography>
      )}
    </Box>
  );
}
